import React, { useState, useEffect, useRef } from "react";
import styles from "./note-input.module.scss";
import plus from "../../../images/plus.svg";
import cross from "../../../images/cross.svg";
import { useTranslation } from "react-i18next";

interface NoteInputProps {
  initialNotes?: string[];
  maxNotes?: number;
  onNotesChange?: (notes: string[]) => void;
  className?: string;
  maxCharacters?: number;
}

const NoteInput: React.FC<NoteInputProps> = ({
  initialNotes = [],
  maxNotes = 3,
  onNotesChange,
  className = "",
  maxCharacters = 140,
}) => {
  const { t } = useTranslation();
  const safeInitialNotes = Array.isArray(initialNotes) ? initialNotes : [];
  const [notes, setNotes] = useState<string[]>(
    safeInitialNotes.length > 0 ? safeInitialNotes : [""],
  );
  const textareasRef = useRef<(HTMLTextAreaElement | null)[]>([]);

  useEffect(() => {
    textareasRef.current.forEach((textarea) => {
      if (textarea) {
        textarea.style.height = "48px";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    });
  }, [notes]);

  useEffect(() => {
    if (initialNotes && initialNotes.length > 0) {
      setNotes(initialNotes);
    } else {
      setNotes([""]);
    }
  }, [initialNotes]);

  const addNote = () => {
    if (notes.length < maxNotes) {
      const newNotes = [...notes, ""];
      setNotes(newNotes);
      if (onNotesChange) onNotesChange(newNotes);
    }
  };

  const clearNotes = () => {
    const newNotes = [""];
    setNotes(newNotes);
    if (onNotesChange) onNotesChange(newNotes);
  };

  const updateNote = (value: string, index: number) => {
    const newNotes = [...notes];
    newNotes[index] = value;
    setNotes(newNotes);
    if (onNotesChange) onNotesChange(newNotes);
  };

  const deleteNote = (index: number) => {
    let newNotes = notes.filter((_, i) => i !== index);
    if (newNotes.length === 0) {
      newNotes = [""];
    }
    setNotes(newNotes);
    if (onNotesChange) onNotesChange(newNotes);
  };

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.header}>
        <label className={styles.label}>{t("note-input.label")}</label>
        {notes.length < maxNotes ? (
          <button onClick={addNote} className={styles.button}>
            <img
              src={plus}
              alt={t("note-input.alt.add")}
              className={styles.icon}
            />
            {t("note-input.button.addNew")}
          </button>
        ) : (
          <button onClick={clearNotes} className={styles.button}>
            <img
              src={cross}
              alt={t("note-input.alt.clear")}
              className={styles.icon}
            />
            {t("note-input.button.clearAll")}
          </button>
        )}
      </div>
      {notes.map((note, index) => (
        <div key={index} className={`${styles.noteWrapper} ${styles.active}`}>
          <div className={styles.inputContainer}>
            <textarea
              className={`${styles.input} ${note ? styles.active : ""}`}
              value={note}
              onChange={(e) => updateNote(e.target.value, index)}
              placeholder={t("note-input.placeholder")}
              maxLength={maxCharacters}
              ref={(el) => (textareasRef.current[index] = el)}
            />
            <div className={styles.controls}>
              <span className={styles.charCount}>
                {maxCharacters - note.length}
              </span>
              {note && (
                <button
                  onClick={() => deleteNote(index)}
                  className={styles.clearButton}
                >
                  <img
                    src={cross}
                    alt={t("note-input.alt.clear")}
                    loading="eager"
                  />
                </button>
              )}
            </div>
          </div>
          {notes.length === 1 && (
            <p className={styles.noteHint}>{t("note-input.noteHint")}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default NoteInput;
